import React, { useCallback, useEffect, useState } from 'react';
import { ShoppingBagIcon, XIcon, CheckCircleIcon } from 'lucide-react';
const FIRST_NAMES = ['Emily', 'James', 'Sarah', 'Michael', 'Jessica', 'David', 'Ashley', 'Chris', 'Amanda', 'Daniel', 'Megan', 'Tyler', 'Brittany', 'Ryan', 'Lauren', 'Kevin'];
const LAST_NAMES = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Wilson', 'Taylor', 'Anderson', 'Thomas', 'Jackson', 'White', 'Harris', 'Martin'];
const PROVINCES = ['California', 'Texas', 'Florida', 'New York', 'Pennsylvania', 'Ohio', 'Illinois', 'Georgia', 'North Carolina', 'Michigan', 'New Jersey', 'Virginia', 'Washington', 'Arizona', 'Tennessee', 'Colorado', 'Oregon', 'Minnesota'];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function generateNotification() {
  const firstName = pickRandom(FIRST_NAMES);
  const lastName = pickRandom(LAST_NAMES);
  const province = pickRandom(PROVINCES);
  const minutes = Math.floor(Math.random() * 14) + 1;
  return {
    firstName,
    lastName,
    province,
    minutes
  };
}
type Notification = ReturnType<typeof generateNotification>;
export function PurchasePopup() {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [current, setCurrent] = useState<Notification | null>(null);
  const showNext = useCallback(() => {
    setCurrent(generateNotification());
    setExiting(false);
    setVisible(true);
  }, []);
  const hideNotification = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, 400);
  }, []);
  useEffect(() => {
    if (dismissed) return;
    const initialTimer = setTimeout(() => {
      showNext();
    }, 3000);
    return () => clearTimeout(initialTimer);
  }, [dismissed, showNext]);
  useEffect(() => {
    if (dismissed || !visible || exiting) return;
    const hideTimer = setTimeout(() => {
      hideNotification();
    }, 5000);
    return () => clearTimeout(hideTimer);
  }, [visible, exiting, dismissed, hideNotification]);
  useEffect(() => {
    if (dismissed || visible || !current) return;
    const nextDelay = 3000 + Math.random() * 1000;
    const nextTimer = setTimeout(() => {
      showNext();
    }, nextDelay);
    return () => clearTimeout(nextTimer);
  }, [visible, dismissed, current, showNext]);
  if (dismissed || !visible || !current) return null;
  return <div className={`fixed bottom-6 left-6 z-50 max-w-xs w-full ${exiting ? 'popup-exit' : 'popup-enter'}`} role="status" aria-live="polite" data-id="element-180">
      <div className="bg-gray-900 border border-amber-500/30 rounded-xl shadow-2xl shadow-black/40 p-4 flex items-start gap-3" data-id="element-181">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center relative" data-id="element-182">
          <ShoppingBagIcon className="w-5 h-5 text-amber-400" data-id="element-183" />
          <CheckCircleIcon className="w-4 h-4 text-green-400 absolute -bottom-0.5 -right-0.5 fill-gray-900" data-id="element-184" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-185">
          <p className="text-sm text-white leading-snug" data-id="element-186">
            <span className="font-semibold text-amber-300" data-id="element-187">
              {current.firstName} {current.lastName}
            </span>{' '}
            <span className="text-gray-300" data-id="element-188">from</span>{' '}
            <span className="font-medium text-white" data-id="element-189">{current.province}</span>{' '}
            <span className="text-gray-300" data-id="element-190">just purchased the</span>{' '}
            <span className="font-semibold text-amber-400" data-id="element-191">Drama Toolkit</span>
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-192">
            {current.minutes} {current.minutes === 1 ? 'minute' : 'minutes'} ago
          </p>
        </div>

        {/* Close */}
        <button onClick={() => setDismissed(true)} className="flex-shrink-0 text-gray-500 hover:text-gray-300 transition-colors p-0.5 rounded" aria-label="Close notification" data-id="element-193">
          <XIcon className="w-4 h-4" data-id="element-194" />
        </button>
      </div>
    </div>;
}